import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GitMerge } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MergeStudentsDialog({ open, onClose, students, onMerged }) {
  const [primaryId, setPrimaryId] = useState(null);
  const [merging, setMerging] = useState(false);
  const token = localStorage.getItem("session_token");

  useEffect(() => {
    if (!open) return;
    const best = [...students].sort((a, b) =>
      (b.account_type === "full") - (a.account_type === "full") ||
      (b.completed_course_ids.length + b.in_progress_course_ids.length) - (a.completed_course_ids.length + a.in_progress_course_ids.length)
    )[0];
    setPrimaryId(best?.id || null);
  }, [open, students]);

  const handleMerge = async () => {
    if (!primaryId) return;
    setMerging(true);
    try {
      const res = await axios.post(
        `${API}/admin/students/merge`,
        { primary_id: primaryId, duplicate_ids: students.filter((s) => s.id !== primaryId).map((s) => s.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      onMerged?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-students-dialog">
        <DialogHeader>
          <DialogTitle>Merge {students.length} student records</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Choose the record to <strong>keep</strong>. All course progress, assessment attempts, certificates, survey responses and
          emails from the other records will move onto it, and the other records will be deleted. Empty profile fields are filled in
          from the merged records.
        </p>
        <div className="space-y-2 my-2">
          {students.map((s) => (
            <label key={s.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${primaryId === s.id ? "border-[#102862] bg-blue-50" : "border-slate-200"}`} data-testid={`merge-option-${s.id}`}>
              <input type="radio" name="primary" checked={primaryId === s.id} onChange={() => setPrimaryId(s.id)} className="mt-1" />
              <div className="text-sm min-w-0">
                <p className="font-medium" style={{ color: "#092036" }}>{s.name || "—"} <span className="text-slate-500 font-normal">&lt;{s.email}&gt;</span></p>
                <p className="text-xs text-slate-500">
                  {s.account_type === "lead" ? "Quick-register lead" : "Full account"}{s.is_subscriber ? " · Subscriber" : ""} · {s.completed_course_ids.length} completed · {s.in_progress_course_ids.length} in progress
                  {s.created_at ? ` · joined ${new Date(s.created_at).toLocaleDateString()}` : ""}
                </p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={merging} data-testid="merge-cancel">Cancel</Button>
          <Button onClick={handleMerge} disabled={merging || !primaryId} className="gap-2 bg-[#102862] hover:bg-[#1a3a7a] text-white" data-testid="merge-confirm">
            <GitMerge className="w-4 h-4" /> {merging ? "Merging..." : "Merge into selected"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
